import { Flex, Text, type IChatMessage } from '@kognitos/lattice';

import { EIDPSource } from '@/modules/idp/types';
import i18n from '@/shared/i18n';
import { selectIDPPanelOpen, useIDPPanelStore } from '@/shared/stores';
import { extractFileIdFromSource } from '@/shared/utils/file-utils';

import { RainCheckpointCard } from '../cards/RainCheckpointCard';
import { RainReviewCard } from '../cards/RainReviewCard';
import { RainGuideApprovalCard } from '@/modules/rain/agents/cards/RainGuideApprovalCard';
import type {
  IRainToolCallState,
  IRainToolRenderContext,
  TRainToolRenderer,
} from '../../hooks/types';
import {
  selectCheckpointReviewOpen,
  useCheckpointReviewStore,
} from '../checkpoint-review.store';
import {
  buildReviewIdpData,
  buildReviewResultValue,
  countReviewFields,
  detectReviewMode,
  parseCheckpointFacts,
  type TCheckpointDecision,
} from '../astral2-review';

/**
 * A verify_checkpoint `request_review` is an approve/reject gate, kept separate
 * from the IDP document flow:
 *   - with `checkpoint_facts` → a trigger card opening the approval dialog,
 *     where the runtime values are reviewed read-only before deciding.
 *   - bare (message only)     → the decision rendered inline in the chat.
 * The chosen decision rides back through the same `resolveWithValue` save
 * path as a `{ Decision: 'approved' | 'rejected' }` fact. By status:
 *   - running  → the trigger / inline decision.
 *   - success  → read-only "Review submitted" marker.
 *   - other    → generic tool card (failure / cancellation), and while the
 *                result is in flight (isSubmitting) so a re-decision can't
 *                double-submit.
 */
const renderCheckpointReviewCard: TRainToolRenderer = (state, ctx) => {
  if (state.status === 'success') {
    return ctx.renderCustomCard(
      state,
      <RainCheckpointCard variant="resolved" />,
    );
  }
  if (state.status !== 'running' || state.isSubmitting) {
    return ctx.renderGenericCard(state);
  }

  const facts = parseCheckpointFacts(state.args);
  const message =
    typeof state.args.message === 'string' ? state.args.message : undefined;
  const decide = (decision: TCheckpointDecision) =>
    ctx.resolvers.resolveWithValue(
      state.id,
      buildReviewResultValue({
        [i18n.t('outputs.idp.exception.checkpointDecision', { ns: 'outputs' })]:
          decision,
      }),
    );

  if (facts.length === 0) {
    return ctx.renderCustomCard(
      state,
      <Flex direction="column" gap={2}>
        {message ? <Text level="base">{message}</Text> : null}
        <RainCheckpointCard
          variant="approval"
          onApprove={() => void decide('approved')}
          onReject={() => void decide('rejected')}
        />
      </Flex>,
    );
  }

  const openDialog = () =>
    selectCheckpointReviewOpen(useCheckpointReviewStore.getState())({
      message,
      facts,
      onDecide: decide,
    });
  return ctx.renderCustomCard(
    state,
    <Flex direction="column" gap={2}>
      {message ? <Text level="base">{message}</Text> : null}
      <RainCheckpointCard
        variant="trigger"
        factCount={facts.length}
        onOpen={openDialog}
      />
    </Flex>,
  );
};

/**
 * A verify_extraction `request_review` (source + extracted/missing fields)
 * reuses the shared IDP dialog so the rain flow matches the non-rain astral
 * "Provide missing values" experience — the user fills the missing fields and
 * the edits resolve the remote tool via the normal save path. By status:
 *   - running  → the card that opens the IDP dialog.
 *   - success  → read-only "Review submitted" marker.
 *   - other    → generic tool card (failure / cancellation / in-flight).
 */
export function renderRequestReviewCard(
  state: IRainToolCallState,
  ctx: IRainToolRenderContext,
): IChatMessage {
  const mode = detectReviewMode(state.args);
  if (mode === 'unknown') {
    return ctx.renderGenericCard(state);
  }
  if (mode === 'checkpoint') {
    return renderCheckpointReviewCard(state, ctx);
  }

  // MAN-3747: the fields-only "Provide missing values" review card/dialog was
  // removed from the design. Without a document there is nothing to render as
  // a review card, so surface only the agent's question text (if any) and let
  // the user answer in chat; fall back to the generic tool card otherwise.
  const hasSource =
    typeof state.args.source === 'string' &&
    state.args.source.trim().length > 0;
  if (!hasSource) {
    const message =
      typeof state.args.message === 'string' ? state.args.message.trim() : '';
    return message
      ? ctx.renderCustomCard(state, <Text level="base">{message}</Text>)
      : ctx.renderGenericCard(state);
  }

  const idpData = buildReviewIdpData(state.args);
  const fieldCount = countReviewFields(state.args);

  if (state.status === 'success') {
    return ctx.renderCustomCard(
      state,
      <RainReviewCard
        fieldCount={fieldCount}
        context={idpData.context}
        variant="resolved"
      />,
    );
  }
  // Once the result is in flight (isSubmitting, awaiting Rain's echo) the
  // review is not actionable — fall through to the generic "Waiting…" card so a
  // re-opened panel can't double-submit silently.
  if (state.status !== 'running' || state.isSubmitting) {
    return ctx.renderGenericCard(state);
  }

  const source = typeof state.args.source === 'string' ? state.args.source : '';
  const message =
    typeof state.args.message === 'string' ? state.args.message : undefined;
  const openReviewPanel = () =>
    selectIDPPanelOpen(useIDPPanelStore.getState())({
      source: EIDPSource.EXCEPTION,
      idpData,
      documentName: extractFileIdFromSource(source),
      context: idpData.context,
      onSave: editedValues =>
        ctx.resolvers.resolveWithValue(
          state.id,
          buildReviewResultValue(editedValues),
        ),
    });
  return ctx.renderCustomCard(
    state,
    <Flex direction="column" gap={2}>
      {message ? <Text level="base">{message}</Text> : null}
      <RainReviewCard
        fieldCount={fieldCount}
        context={idpData.context}
        onClick={openReviewPanel}
      />
    </Flex>,
  );
}

/**
 * "Edit in chat" on the guide-approval card focuses the single bottom composer
 * so the user can type refinements. The card has no ref to the composer, so
 * target it by its stable test id — the same DOM-locator approach
 * RainRunAssistantBody uses to re-anchor the scroll viewport. Works for both
 * surfaces (run-assistant panel and exception thread) since each renders one
 * `rain-clarifying-input` composer.
 */
const focusComposer = (): void => {
  const host = document.querySelector<HTMLElement>(
    '[data-testid="rain-clarifying-input"]',
  );
  const field = host?.matches('textarea, input')
    ? host
    : host?.querySelector<HTMLElement>('textarea, input');
  field?.focus();
};

/**
 * `request_guide_approval` proposes a resolution-guide entry (by resource
 * `name`) and blocks on the user's approve/reject decision. The card reuses the
 * astral flow's guide widget + approval pills; each decision transitions the
 * entry's state AND resolves the tool call so the agent turn continues.
 */
export function renderGuideApprovalCard(
  state: IRainToolCallState,
  ctx: IRainToolRenderContext,
): IChatMessage {
  const guideEntryName =
    typeof state.args.name === 'string' ? state.args.name : '';
  // Without a resource name there's nothing to fetch or act on — fall back to
  // the generic tool card rather than mounting an empty approval widget.
  if (!guideEntryName) return ctx.renderGenericCard(state);
  return ctx.renderCustomCard(
    state,
    <RainGuideApprovalCard
      guideEntryName={guideEntryName}
      isSubmitting={state.isSubmitting}
      onResolve={approved =>
        ctx.resolvers.resolveWithDecision(state.id, approved)
      }
      onEditInChat={focusComposer}
    />,
  );
}
